import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Popconfirm, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import {
  isLiveGrant,
  listGrants,
  revokeGrant,
  type AclResourceType,
  type GranteeType,
  type GrantView,
} from '@/api/acl';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { useAuth } from '@/auth/OlAuthContext';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';
import { STEP_UP_MESSAGE } from '@/acl/AssignSpokeDrawer';

const { Paragraph, Text } = Typography;

const RESOURCE_TYPES: AclResourceType[] = ['flow', 'work_item', 'entity', 'dataroom_invite'];
const GRANTEE_TYPES: GranteeType[] = ['SPOKE', 'USER', 'ROLE', 'PROJECT'];

/**
 * OPSFL-FLOWOPS-1 C3 — "Access fencing": the read-only ledger of instance grants and
 * confinements (UC1 §4.7, GET /v1/acl/grants — revoked rows included: no-delete means history is
 * queryable) with a revoke action for hub_admin. It makes the fence LEGIBLE: which spoke / user /
 * role / project is confined to what, and which instances they were granted. Writes happen on
 * the surfaces that own the gesture (Flows → Assign to spoke); here you review and revoke.
 * acl.read: hub_admin / hub_operator / auditor; revoke is acl.grants.write — hub_admin, step-up.
 */
export function AccessFencingPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;

  const [grants, setGrants] = useState<GrantView[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [resourceType, setResourceType] = useState<AclResourceType | undefined>('flow');
  const [granteeType, setGranteeType] = useState<GranteeType | undefined>(undefined);
  const [show, setShow] = useState<'live' | 'all'>('live');
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setGrants(await listGrants({ resourceType, granteeType }));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [resourceType, granteeType, message]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () => (show === 'live' ? grants.filter((g) => isLiveGrant(g)) : grants),
    [grants, show],
  );

  const revoke = useCallback(
    async (g: GrantView) => {
      setRevoking(g.grantId);
      try {
        await revokeGrant(g.grantId, 'revoked from Access fencing');
        message.success(
          `${g.grantKind === 'CONFINE' ? 'Confinement' : 'Grant'} revoked — bites on the grantee's next request.`,
        );
        void load();
      } catch (e) {
        const err = normalizeApiError(e);
        if (err.httpStatus === 403 && err.code === 'STEPUP_REQUIRED') {
          message.error(STEP_UP_MESSAGE);
        } else {
          message.error(`${err.code}: ${err.message}`);
        }
      } finally {
        setRevoking(null);
      }
    },
    [load, message],
  );

  const columns: ColumnsType<GrantView> = [
    {
      title: 'Kind',
      dataIndex: 'grantKind',
      width: 100,
      render: (k: GrantView['grantKind']) =>
        k === 'CONFINE' ? <Tag color="volcano">CONFINE</Tag> : <Tag color="blue">ALLOW</Tag>,
    },
    {
      title: 'Grantee',
      key: 'grantee',
      render: (_: unknown, g: GrantView) => (
        <span>
          <Tag style={{ marginRight: 6 }}>{g.granteeType}</Tag>
          <Text code>{g.grantee}</Text>
        </span>
      ),
    },
    {
      title: 'Resource',
      key: 'resource',
      render: (_: unknown, g: GrantView) => (
        <span>
          <Tag style={{ marginRight: 6 }}>{g.resourceType}</Tag>
          {g.resourceId ? (
            <Text code>{g.resourceId}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              every instance of the type (confinement)
            </Text>
          )}
        </span>
      ),
    },
    {
      title: 'Capabilities',
      dataIndex: 'capabilities',
      width: 170,
      render: (caps: string[], g: GrantView) =>
        g.grantKind === 'CONFINE' ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            deny-by-default
          </Text>
        ) : (
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {caps.map((c) => (
              <Tag key={c} style={{ margin: 0 }}>
                {c}
              </Tag>
            ))}
          </span>
        ),
    },
    {
      title: 'Reason',
      dataIndex: 'reasonCode',
      width: 160,
      render: (r: string, g: GrantView) => (
        <span>
          <Text style={{ fontSize: 12 }}>{r}</Text>
          {g.caseRef ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {' '}
              · {g.caseRef}
            </Text>
          ) : null}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_: unknown, g: GrantView) =>
        g.revokedAt ? (
          <Tag>revoked</Tag>
        ) : g.expiresAt && Date.parse(g.expiresAt) <= Date.now() ? (
          <Tag>expired</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
    },
    {
      title: 'Granted',
      key: 'granted',
      width: 190,
      render: (_: unknown, g: GrantView) => (
        <span style={{ fontSize: 12 }}>
          {formatAbsolute(g.grantedAt)}
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            by {g.grantedBy}
          </Text>
        </span>
      ),
    },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_: unknown, g: GrantView) =>
              isLiveGrant(g) ? (
                <Popconfirm
                  title={`Revoke this ${g.grantKind === 'CONFINE' ? 'confinement' : 'grant'}?`}
                  description={
                    g.grantKind === 'CONFINE'
                      ? 'The grantee returns to seeing the whole hub catalogue for this type.'
                      : 'The grantee loses this instance on their next request.'
                  }
                  okText="Revoke"
                  onConfirm={() => void revoke(g)}
                >
                  <Button size="small" danger loading={revoking === g.grantId}>
                    Revoke
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]
      : []),
  ];

  if (unavailable) {
    return (
      <EmptyState
        title="Access fencing is not available"
        description="The instance-ACL surface (/v1/acl) is not enabled in this deployment."
      />
    );
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 820, fontSize: 13 }}>
        The access fence, made legible: a <b>confinement</b> row means the grantee sees and acts on
        nothing of that type until granted; an <b>allow</b> row grants one instance with named
        capabilities. Assign a flow to a spoke from Flows (the row&apos;s shield); review and revoke
        every fence here. Revocation bites on the grantee&apos;s next request — no re-login. Rows
        are never deleted: switch to <i>All</i> to read the history.
      </Paragraph>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select<AclResourceType | undefined>
          allowClear
          placeholder="Resource type"
          value={resourceType}
          onChange={(v) => setResourceType(v)}
          options={RESOURCE_TYPES.map((t) => ({ value: t, label: t }))}
          style={{ width: 170 }}
          aria-label="Resource type"
        />
        <Select<GranteeType | undefined>
          allowClear
          placeholder="Grantee type"
          value={granteeType}
          onChange={(v) => setGranteeType(v)}
          options={GRANTEE_TYPES.map((t) => ({ value: t, label: t }))}
          style={{ width: 150 }}
          aria-label="Grantee type"
        />
        <Segmented
          options={[
            { label: 'Live', value: 'live' },
            { label: 'All (incl. revoked)', value: 'all' },
          ]}
          value={show}
          onChange={(v) => setShow(v as 'live' | 'all')}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void load()} aria-label="Reload">
          Reload
        </Button>
      </Space>
      <Table<GrantView>
        rowKey="grantId"
        size="small"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{
          emptyText: (
            <EmptyState
              compact
              title={show === 'live' ? 'No live fences' : 'No fences recorded'}
              description="Nothing is fenced for this filter — every holder of the matrix keys sees the whole hub. Assign a flow to a spoke from Flows to create the first one."
            />
          ),
        }}
      />
    </div>
  );
}
